import { MdAdd, MdModeEditOutline, MdSearch } from "react-icons/md";
import { FiInfo, FiTrash2 } from "react-icons/fi";
import { useClient } from "../ClientMaster/Hooks/useClient";
import { useState, useEffect } from "react";
import { Info, Edit, Trash2, } from "lucide-react";
import {
    GenericTable,
    Pagination,
    Spinner,
} from "../../Components/Common";
import {
    CLIENT_COLUMNS,
    CLIENT_LABELS,
} from "./constants";
import {
    CREATE_CLIENT,
    DETAIL_CLIENT,
    EDIT_CLIENT,
} from "../../routes";
import { useNavigate } from "react-router-dom";
import { TOAST_MESSAGES } from "./constants";

const ClientMasterList = () => {
  const navigate = useNavigate();
  const {
    loading,
    page,
    totalPages,
    rowsPerPage,
    filters,
    setFilters,
    onPageChange,
    onPageSizeChange,
    fetchAllClients,
    removeClient,
  } = useClient();

  const [clients, setClients] = useState([]);

  const fetchClient = async () => {
    const data = await fetchAllClients();
    setClients(data || []);
  };

  const handleFilterChange = (key, value) => {
    const updated = { ...filters, [key]: value };
    setFilters(updated);
    onPageChange(1);
  };

  const handleAddClient = () => navigate(CREATE_CLIENT);
  const handleDetailClient = (id) =>
    navigate(DETAIL_CLIENT.replace(":instanceId", id));
  const handleEditClient = (id) =>
    navigate(EDIT_CLIENT.replace(":instanceId", id));

  const handleDeleteClient = async (id) => {
    const confirmed = window.confirm(TOAST_MESSAGES.CLIENT_DELETE_ALERT);
    if (!confirmed) return;

    const success = await removeClient(id);
    if (success) fetchClient();
  };

  // Mobile card rendering
  const renderMobileFields = (clients) => (
    <div className="mb-2 relative p-2 border rounded-md bg-white mt-2">
      <div
        onClick={() => handleDetailClient(clients.id)}
        className="p-4 bg-white rounded-lg shadow cursor-pointer hover:shadow-md transition-shadow"
      >
        <div className="flex justify-between items-start">
          <div className="text-md font-bold text-gray-900 truncate">
            {clients.name}
          </div>
          <div
            className="flex items-center gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-row justify-end items-center gap-4">
              <span>
                {/* Edit Button */}
                <MdModeEditOutline
                  size={18}
                  className="h-5 w-5 text-blue-500 cursor-pointer hover:text-blue-700 transition-colors"
                  onClick={() => handleEditClient(clients.id)}
                />
              </span>
              <span>
                {/* Delete Button */}
                <FiTrash2
                  size={18}
                  className="cursor-pointer text-red-500 hover:text-red-700"
                  onClick={() => handleDeleteClient(clients.id)}
                />
              </span>
            </div>
          </div>
        </div>
        <div className="flex justify-between items-start">
          <div className="text-sm text-gray-700 mt-1">{clients.mobile}</div>
          <div
            className="text-sm text-gray-700 mt-1 max-w-[150px] truncate"
            title={clients.address}
          >
            {clients.address || "-"}
          </div>
        </div>
      </div>
    </div>
  );

  // Table field rendering
  const renderFields = (key, row) => {
    switch (key) {
      case "name":
        return row.name || "-";
      case "contact_person_name":
        return row.contact_person_name;
      case "mobile":
        return row.mobile;
      case "address":
        return row.address || "-";
      case "gst_details":
        return row.gst_details || "-";
      case "actions":
        return (
          <div className="flex items-center gap-4">
            {/* Detail Button */}
            <FiInfo
              size={17}
              onClick={() => handleDetailClient(row.id)}
              className="text-blue-500 cursor-pointer hover:text-blue-700 transition-colors"
            />
            {/* Edit Button */}
            <MdModeEditOutline
              size={17}
              onClick={() => handleEditClient(row.id)}
              className="text-blue-500 cursor-pointer hover:text-blue-700 transition-colors"
            />
            {/* Delete button */}
            <FiTrash2
              size={17}
              className="cursor-pointer text-red-500 hover:text-red-700"
              onClick={() => handleDeleteClient(row.id)}
            />
          </div>
        );
      default:
        return row[key] || "-";
    }
  };

  useEffect(() => {
    fetchClient();
  }, [page, rowsPerPage, filters]);

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          Client Master
        </h1>
        <button
          onClick={handleAddClient}
          className="bg-primary text-white px-2 py-1 rounded-md flex items-center gap-2"
        >
          <MdAdd size={18} />
          <span className="whitespace-nowrap">Add Client</span>
        </button>
      </div>

      <div className="bg-white w-full rounded-2xl shadow-md border px-4 py-3">
        <div className="relative md:w-1/4 w-3/4">
          <MdSearch
            size={20}
            className="absolute md:left-2 mt-2 left-2 text-gray-500"
          />
          <input
            type="text"
            name="search"
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            placeholder="Search by client name"
            className="w-full rounded-lg border border-gray-300 bg-white text-sm pl-9 pr-8 placeholder-gray-400 focus:ring-1 focus:border-blue-500 outline-none transition duration-150"
          />
          {filters.search && (
            <button
              type="button"
              onClick={() => handleFilterChange("search", "")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-x-auto sm:rounded-lg mt-2">
      {loading ? (
        <div className="flex justify-center items-center h-[30vh]">
          <Spinner />
        </div>
      ) : (
        <div>
          {/* Desktop View */}
          <div className="md:block md:h-[53vh] h-[60vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
            <GenericTable
              tableColumns={CLIENT_COLUMNS}
              tableRows={clients}
              columnLabels={CLIENT_LABELS}
              renderField={renderFields}
              enableGridLayout={true}
              mobileGridColumns={1}
              renderMobileCard={renderMobileFields}
            />
          </div>

          {/* Pagination */}
          <div className="pt-3">
            <Pagination
              page={page}
              rowsPerPage={rowsPerPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
            />
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default ClientMasterList;
